import InteractiveAnnotation from "./interactiveAnnotation";
import {  measureText, setFontStyle, setLineStyle, drawBox } from "../canvasUtils";
import { isInBoundsBox, isInBoundsLine } from "../geometry";
import MeasurementText from "./measurementText";

export default class Rectangle extends InteractiveAnnotation {
  constructor(position, options) {
    super(position, options);

    this.topLeftX = position.x;
    this.topLeftY = position.y;
    this.contactPoint = { x: 0, y: 0 };
    this.distanceFromTopLeft = { x: 0, y: 0 };

    this.height = 70;
    this.width = 70;
    this.area = 0;

    this.minWidth = 10;
    this.minHeight = 10;

    this.activeMovement = false;
    this.activeCorner = false;

    this.activeText = false;

    this.measurementTexts = {
      area: new MeasurementText(),
      width: new MeasurementText(),
      height: new MeasurementText()
    } 
  }

  get isActive() {
    return this.activeMovement || !!this.activeCorner || !!this.activeText;
  }

  get isTextActive() {
    return !!this.activeText;
  }

  get bottomRightX() {
    return this.topLeftX + this.width;
  }

  get bottomRightY() {
    return this.topLeftY + this.height;
  }

  get topLeft() {
    return { x: this.topLeftX, y: this.topLeftY };
  }

  get topRight() {
    return { x: this.bottomRightX, y: this.topLeftY };
  }

  get bottomLeft() {
    return { x: this.topLeftX, y: this.bottomRightY };
  }

  get bottomRight() {
    return { x: this.bottomRightX, y: this.bottomRightY };
  }

  get widthTextPosition(){
    let x = this.topLeftX;
    let y = this.topLeftY - 25;
    return {x, y};
  }

  get widthText(){
    let length = Math.abs(this.scale * this.width);
    return length.toFixed(2) + " mm";
  }

  get widthTextWidth(){
    return measureText(this.widthText, this.font);
  }

  get heightTextPosition(){
    let x = this.topLeftX + this.width + 5;
    let y = this.topLeftY + (this.height / 2) - 10;
    return {x, y};
  }

  get heightText(){
    let length = Math.abs(this.scale * this.height);
    return length.toFixed(2) + " mm";
  }

  get heightTextWidth(){
    return measureText(this.heightText, this.font);
  }

  get areaTextPosition(){
    let x = this.topLeftX + 5;
    let y = this.topLeftY + this.height/2 + 10;
    return {x, y};
  }

  get areaText(){
    let area = Math.abs((this.scale * this.width) * (this.scale * this.height));
    return area.toFixed(2) + " mm²";
  }

  get areaTextWidth(){
    return measureText(this.areaText, this.font);
  }

  draw(ctx) {
    this.drawRect(ctx);
    this.drawText(ctx);
    if (this.isActive){
      if(this.isTextActive) this.drawTextInteractionBoxes(ctx) 
      else this.drawInteractionBoxes(ctx)
    }       
  }

  drawRect(ctx) {
    setLineStyle(ctx, this);
    ctx.strokeRect(this.topLeftX, this.topLeftY, this.width, this.height);
  }

  drawText(ctx){
    setFontStyle(ctx, this);

    this.measurementTexts.area.updateAndDraw(this.areaTextWidth, this.textSize, ctx, this.areaText, this.areaTextPosition.x, this.areaTextPosition.y);
    this.measurementTexts.width.updateAndDraw(this.widthTextWidth, this.textSize, ctx, this.widthText, this.widthTextPosition.x, this.widthTextPosition.y);
    this.measurementTexts.height.updateAndDraw(this.heightTextWidth, this.textSize, ctx, this.heightText, this.heightTextPosition.x, this.heightTextPosition.y);
  }

  drawInteractionBoxes(ctx) {
    drawBox(ctx, this.topLeftX, this.topLeftY, this.transform.zoom);
    drawBox(ctx, this.topLeftX, this.bottomRightY, this.transform.zoom);
    drawBox(ctx, this.bottomRightX, this.topLeftY, this.transform.zoom);
    drawBox(ctx, this.bottomRightX, this.bottomRightY, this.transform.zoom);
  }

  drawTextInteractionBoxes(ctx){  
    this.measurementTexts[this.activeText].drawTextInteractionBoxe(ctx);
  }

  isInBounds(x, y) {
    return this.isInBoundsResizeCorner(x, y) || this.isOnPerimeter(x, y) || this.isInBoundsAllTexts(x, y);
  }

  isInBoundsAllTexts(x, y) {
    return this.measurementTexts.height.isInBound(x, y)
      || this.measurementTexts.width.isInBound(x, y)
      || this.measurementTexts.area.isInBound(x, y);
  }

  isInBoundsResizeCorner(x, y) {
    return this.isInBoundsCorner(x, y, this.topLeft) 
      || this.isInBoundsCorner(x, y, this.topRight)
      || this.isInBoundsCorner(x, y, this.bottomRight)
      || this.isInBoundsCorner(x, y, this.bottomLeft);
  }

  isOnPerimeter(x, y) {
    return isInBoundsLine(x, y, this.topLeft.x, this.topLeft.y, this.topRight.x, this.topRight.y)
      || isInBoundsLine(x, y, this.topLeft.x, this.topLeft.y, this.bottomLeft.x, this.bottomLeft.y)
      || isInBoundsLine(x, y, this.bottomRight.x, this.bottomRight.y, this.topRight.x, this.topRight.y)
      || isInBoundsLine(x, y, this.bottomRight.x, this.bottomRight.y, this.bottomLeft.x, this.bottomLeft.y);
  }

  isInBoundsCorner(x, y, { x: cx, y: cy }) {
    return isInBoundsBox(x, y, cx, cy, this.transform.zoom);
  }

  mousedown(x, y){
    this.clearActiveStates();
    if (this.isInBoundsCorner(x, y, this.topRight)) {
      this.activeCorner = "TR";
    } else if (this.isInBoundsCorner(x, y, this.bottomRight)) {
      this.activeCorner = "BR";
    } else if (this.isInBoundsCorner(x, y, this.bottomLeft)) {
      this.activeCorner = "BL";
    } else if (this.isInBoundsCorner(x, y, this.topLeft)) {
      this.activeCorner = "TL";
    } else if (this.measurementTexts.area.isInBound(x, y)){
      this.activeText = "area";
    } else if (this.measurementTexts.width.isInBound(x, y)){
      this.activeText = "width";
    } else if (this.measurementTexts.height.isInBound(x, y)){
      this.activeText = "height";
    }
    else {
      this.contactPoint.x = x;
      this.contactPoint.y = y;
      this.distanceFromTopLeft.x = this.contactPoint.x - this.topLeftX;
      this.distanceFromTopLeft.y = this.contactPoint.y - this.topLeftY;
      this.activeMovement = true;
    }
    this.signalReady();
  }
  
  mousemove(x, y) {
    if (this.activeMovement) this.updateLocation(x, y);
    else if (this.activeCorner) this.updateSize(x, y);
    else if (this.activeText) this.updateTextLocation(x, y);
    this.signalReady();
  }

  updateLocation(x, y) {
    this.topLeftX = x - this.distanceFromTopLeft.x;
    this.topLeftY = y - this.distanceFromTopLeft.y;
  }

  updateSize(x, y) {
    let width = 0;
    let height = 0;

    if (this.activeCorner.includes("L")) {
      width = Math.max(this.bottomRightX - x, this.minWidth);
      if (width > this.minWidth) this.topLeftX = x;
    }
    else if (this.activeCorner.includes("R")) width = Math.max(x - this.topLeftX, this.minWidth);

    this.width = width;

    if (this.activeCorner.includes("B")) height = Math.max(y - this.topLeftY, this.minHeight);

    if (this.activeCorner.includes("T")) {
      height = Math.max(this.bottomRightY - y, this.minHeight);
      if (height > this.minHeight) this.topLeftY = y;
    }

    this.height = height;
  }

  updateTextLocation(x, y){
    this.measurementTexts[this.activeText].updateLocation(x, y)
  }

  deactivate() {
    this.clearActiveStates();
    this.signalReady();
  }

  clearActiveStates() {
    this.activeMovement = false;
    this.activeCorner = null;
    this.activeText = null;
    this.contactPoint.x = 0;
    this.contactPoint.y = 0;
    this.distanceFromTopLeft.x = 0;
    this.distanceFromTopLeft.y = 0;
  }
}
